import { newTheme } from '@customerio/pluma-components/css';
import customerIoLogo from '@/assets/images/logos/thumbnail-logo-navigation.svg';
import { useAuth } from '@/services/auth';
import { useCurrentWorkspace } from '@/context/current-workspace';
import type { TargetTransitionOptions } from '@/services/router/safe-navigate';
import { WorkspaceDropdown } from './components/workspace-selector';
import { PlainButton, Image, Box, Icon, Popover, Text, useSideNavState } from '@customerio/pluma-components/react';
import { PlumaSideNav as PlumaSideNavCss } from '@customerio/pluma-components/css/components';
import { AnimatePresence, m } from 'motion/react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import * as styles from './workspace-selector.css';
import { MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION } from '@/utils/motion';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import { AccountSwitcherContext, SESSION_METADATA_REFETCH_INTERVAL } from '@/features/account-switcher';
import { useServicesQuery } from '@/services/api';
import { updateSessions } from '@customerio/hydra-session-state';

const SIDE_NAV_PADDING = Number.parseFloat(newTheme.tokensMeta['space-150'].$value);
const EXPANDED_WIDTH = Number.parseFloat(PlumaSideNavCss.values.expandedWidth) - SIDE_NAV_PADDING * 2;
const CONTENT_GAP = Number.parseFloat(newTheme.tokensMeta['space-100'].$value);
const CONTENT_GAP_COLLAPSED = 18;
const CHEVRON_WIDTH =
  Number.parseFloat(newTheme.tokensMeta['space-icon-v2-md'].$value) +
  Number.parseFloat(newTheme.tokensMeta['space-100'].$value);

function useCollapsedWidth() {
  const measureRef = useRef<HTMLDivElement>(null);
  const [collapsedWidth, setCollapsedWidth] = useState<number | null>(null);

  useMountEffect(() => {
    const element = measureRef.current;
    if (element == null) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      if (entry != null) {
        setCollapsedWidth(Math.ceil(entry.contentBoxSize[0].inlineSize));
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  });

  return { measureRef, collapsedWidth };
}

const buttonVariants = {
  expanded: {
    width: EXPANDED_WIDTH,
    transition: MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION,
  },
  collapsed: (collapsedWidth: number | null) => ({
    width: collapsedWidth ?? undefined,
    transition: MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION,
  }),
};

const contentSpacerVariants = {
  expanded: {
    width: CONTENT_GAP,
    transition: MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION,
  },
  collapsed: {
    width: CONTENT_GAP_COLLAPSED,
    transition: MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION,
  },
};

const chevronVariants = {
  initial: {
    opacity: 0,
    width: 0,
  },
  animate: {
    opacity: 1,
    width: CHEVRON_WIDTH,
    // Typically we'd use enter/exit easings for appearing/disappearing
    // content, but we want this transition to be in sync with the button itself
    transition: MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION,
  },
  exit: {
    opacity: 0,
    width: 0,
    transition: MODERATE_01_STANDARD_PRODUCTIVE_TRANSITION,
  },
};

function useSessionMetadataSync() {
  const { data, isLoading, error } = useServicesQuery(
    'getSessionMetadata',
    {},
    {
      refetchInterval: SESSION_METADATA_REFETCH_INTERVAL,
      refetchOnMount: 'always',
      refetchOnWindowFocus: 'always',
    },
  );

  useEffect(() => {
    if (isLoading || error != null || data == null) {
      return;
    }

    updateSessions(data);
  }, [data, isLoading, error]);
}

function getLogoForNav(logoUrl: string | null | undefined) {
  if (logoUrl == null || logoUrl === '') {
    return customerIoLogo;
  }
  return logoUrl;
}

function useDisplayInfo() {
  const auth = useAuth();
  const currentWorkspace = useCurrentWorkspace();
  return {
    logoForNav: getLogoForNav(auth.account?.logo_url),
    accountName: auth.account?.name,
    workspaceName: currentWorkspace?.name,
  };
}

function useIsSideNavCollapsed() {
  const sideNavState = useSideNavState();
  return sideNavState?.isCollapsed ?? false;
}

type PopoverChildrenFunction = Extract<
  NonNullable<React.ComponentProps<typeof Popover>['children']>,
  (...args: never[]) => unknown
>;
type PopoverChildrenContext = Parameters<PopoverChildrenFunction>[0];

function WorkspaceSelectorTrigger(props: {
  context: PopoverChildrenContext;
  isSideNavCollapsed: boolean;
  variantName: 'collapsed' | 'expanded';
  collapsedWidth: number | null;
  logoForNav: string;
  accountName: string | undefined;
  workspaceName: string | undefined;
}) {
  return (
    <PlainButton
      as={m.button}
      className={styles.workspaceSelectorButton}
      aria-label={`${props.workspaceName} workspace`}
      aria-description="Click to open workspace selector."
      ref={props.context.reference.setReference}
      {...props.context.reference.getReferenceProps()}
      initial={false}
      animate={props.variantName}
      custom={props.collapsedWidth}
      variants={buttonVariants}
    >
      <m.div initial={false} className={styles.workspaceSelectorButtonContent}>
        <WorkspaceSelectorButtonContent
          logoForNav={props.logoForNav}
          accountName={props.accountName}
          workspaceName={props.workspaceName}
          spacer={
            <m.div
              className={styles.workspaceSelectorButtonContentSpacer}
              initial={false}
              variants={contentSpacerVariants}
            />
          }
        />
      </m.div>

      <AnimatePresence initial={false}>
        {props.isSideNavCollapsed ? null : (
          <m.div
            className={styles.workspaceSelectorButtonChevronContainer}
            variants={chevronVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <Icon name="chevron-toggle" flexShrink="0" />
          </m.div>
        )}
      </AnimatePresence>
    </PlainButton>
  );
}

const WorkspaceSelectorMeasure = forwardRef<
  HTMLDivElement,
  { logoForNav: string; accountName: string | undefined; workspaceName: string | undefined }
>(function WorkspaceSelectorMeasure(props, ref) {
  return (
    <div ref={ref} aria-hidden="true" className={styles.workspaceSelectorMeasure}>
      <div className={styles.workspaceSelectorButton}>
        <div className={styles.workspaceSelectorButtonContent}>
          <WorkspaceSelectorButtonContent
            logoForNav={props.logoForNav}
            accountName={props.accountName}
            workspaceName={props.workspaceName}
            spacer={
              <div className={styles.workspaceSelectorButtonContentSpacer} style={{ width: CONTENT_GAP_COLLAPSED }} />
            }
          />
        </div>
      </div>
    </div>
  );
});

function WorkspaceSelectorButtonContent(props: {
  logoForNav: string;
  accountName: string | undefined;
  workspaceName: string | undefined;
  spacer: React.ReactNode;
}) {
  return (
    <>
      <Box className={styles.workspaceSelectorButtonImageContainer}>
        <Image src={props.logoForNav} alt="" width={24} height={24} flexShrink="0" />
      </Box>

      {props.spacer}

      <Box flexGrow="1">
        <Text className={styles.workspaceSelectorAccountName} display="block" size="xxs" color="subtle" truncate={true}>
          {props.accountName}
        </Text>
        <Text
          className={styles.workspaceSelectorWorkspaceName}
          display="block"
          text="product-ps-active"
          truncate={true}
        >
          {props.workspaceName}
        </Text>
      </Box>
    </>
  );
}

export function WorkspaceSelector(props: {
  onLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, options?: TargetTransitionOptions) => void;
}) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isSwitchingAccounts, setIsSwitchingAccounts] = useState(false);
  const { measureRef, collapsedWidth } = useCollapsedWidth();
  useSessionMetadataSync();

  const isSideNavCollapsed = useIsSideNavCollapsed();
  const { logoForNav, accountName, workspaceName } = useDisplayInfo();

  const onWorkspaceLinkClick = async (
    event: React.MouseEvent<HTMLAnchorElement>,
    options?: TargetTransitionOptions,
  ) => {
    props.onLinkClick(event, options);
    setIsPopoverOpen(false);
  };

  const onPopoverOpenChange = (state: boolean) => {
    setIsPopoverOpen(state);
    setIsSwitchingAccounts(false);
  };

  const variantName = isSideNavCollapsed ? 'collapsed' : 'expanded';

  return (
    <>
      <Popover isOpen={isPopoverOpen} onOpenChange={onPopoverOpenChange} placement="bottom-start" withArrow={false}>
        {(context) => (
          <>
            <WorkspaceSelectorTrigger
              context={context}
              isSideNavCollapsed={isSideNavCollapsed}
              variantName={variantName}
              collapsedWidth={collapsedWidth}
              logoForNav={logoForNav}
              accountName={accountName}
              workspaceName={workspaceName}
            />
            <AccountSwitcherContext.Provider value={{ isSwitchingAccounts, setIsSwitchingAccounts }}>
              <WorkspaceDropdown onWorkspaceLinkClick={onWorkspaceLinkClick} />
            </AccountSwitcherContext.Provider>
          </>
        )}
      </Popover>

      <WorkspaceSelectorMeasure
        ref={measureRef}
        logoForNav={logoForNav}
        accountName={accountName}
        workspaceName={workspaceName}
      />
    </>
  );
}
